import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Input } from '@appsumo/dorado-react';
import parsePhoneNumberWithError, {
  CountryCode,
  PhoneNumber,
  getExampleNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { useFormContext } from 'react-hook-form';

import { PhoneInputProps } from '~/types/global';

export function PhoneInput({
  selectedCountry,
  name,
  label,
  defaultNumber,
  disabled,
  onPhoneNumberChange,
  hideBorder = true,
}: Readonly<PhoneInputProps>) {
  const [prevCountry, setPrevCountry] = useState<CountryCode>(selectedCountry);
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | undefined>(
    parsePhoneNumberWithError(
      defaultNumber?.nationalNumber ?? '',
      selectedCountry,
    ),
  );
  const form = useFormContext();

  const updatePhoneNumber = useCallback(
    (value: string) => {
      const parsedPhoneNumber = parsePhoneNumberWithError(
        value,
        selectedCountry,
      );
      setPhoneNumber(parsedPhoneNumber);
      form?.setValue(name, parsedPhoneNumber?.number);
      // additonal callback for parent component
      onPhoneNumberChange?.(parsedPhoneNumber?.number ?? '');
    },
    [selectedCountry, name, form, onPhoneNumberChange],
  );

  const onPhoneInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updatePhoneNumber(e.target.value);
    },
    [updatePhoneNumber],
  );

  useEffect(() => {
    if (prevCountry !== selectedCountry) {
      setPrevCountry(selectedCountry);
      updatePhoneNumber(defaultNumber?.nationalNumber ?? '');
    }
  }, [prevCountry, selectedCountry, defaultNumber, updatePhoneNumber]);

  return (
    <div className="inline-block w-full">
      <Input
        name={`${name}__proxy`}
        label={label}
        type="text"
        placeholder={getExampleNumber(
          selectedCountry,
          examples,
        )?.formatNational()}
        defaultValue={defaultNumber?.formatNational()}
        data-testid="phone-input"
        onChange={onPhoneInputChange}
        hideBorder={hideBorder}
        disabled={disabled}
        inputClasses="px-2 py-2 rounded-r outline-none"
      />
      {/* hidden input, this allows us to have the 'proper' formating on the number, without showing it to the user */}
      <input
        type="hidden"
        data-testid="phone-input-hidden"
        defaultValue={phoneNumber?.number}
        {...form?.register(name)}
      />
    </div>
  );
}
